import React, { useMemo } from "react";
import { PlusIcon, PencilIcon, InformationCircleIcon, ClockIcon } from "@heroicons/react/24/outline";
import { FaUsers } from "react-icons/fa";
import { StatusBadge, StatCard } from "./Shared";
import { normalizeStatus, formatDate } from "./utils";

const UsersListModal = ({
    open,
    server,
    users,
    onClose,
    onAddUser,
    onEditUser,
    onToggleStatus,
    onViewDetails,
    onViewHistory,
}) => {
    const stats = useMemo(() => {
        const total = users.length;
        const active = users.filter((u) => normalizeStatus(u.status) === "active").length;
        return { total, active, inactive: total - active };
    }, [users]);

    if (!open || !server) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-xl bg-white border border-slate-200 p-6" style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.15)" }}>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <FaUsers className="w-5 h-5 text-blue-600" />
                        Users — {server.server_name}
                    </h2>
                    <div className="flex items-center gap-2">
                        <button onClick={() => onAddUser(server)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white" style={{ background: "linear-gradient(90deg, #1e40af, #0369a1)" }}>
                            <PlusIcon className="w-3.5 h-3.5" />
                            Add User
                        </button>
                        <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                    <StatCard label="Total Users" value={stats.total} colorClass="bg-indigo-50 text-indigo-700 border border-indigo-200" dotClass="bg-indigo-500" />
                    <StatCard label="Active Users" value={stats.active} colorClass="bg-emerald-50 text-emerald-700 border border-emerald-200" dotClass="bg-emerald-500" />
                    <StatCard label="Inactive Users" value={stats.inactive} colorClass="bg-rose-50 text-rose-700 border border-rose-200" dotClass="bg-rose-500" />
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                            <tr>
                                <th className="p-3 text-left border-b border-slate-200">Company Name</th>
                                <th className="p-3 text-left border-b border-slate-200">Contact Person</th>
                                <th className="p-3 text-left border-b border-slate-200">Contact Number</th>
                                <th className="p-3 text-left border-b border-slate-200">User Name</th>
                                <th className="p-3 text-left border-b border-slate-200">User Password</th>
                                <th className="p-3 text-left border-b border-slate-200">Licence Type</th>
                                <th className="p-3 text-center border-b border-slate-200">Status</th>
                                <th className="p-3 text-center border-b border-slate-200">Activation Date</th>
                                <th className="p-3 text-center border-b border-slate-200">Expire Date</th>
                                <th className="p-3 text-center border-b border-slate-200">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="p-8 text-center text-slate-400 text-sm">No users found for this server.</td>
                                </tr>
                            ) : (
                                users.map((user) => (
                                    <tr key={user.slNo} className="border-t border-slate-100 hover:bg-blue-50/60">
                                        <td className="p-3 text-slate-800 text-xs">{user.company_name}</td>
                                        <td className="p-3 text-slate-800 text-xs">{user.contact_person_name}</td>
                                        <td className="p-3 text-slate-500 text-xs">{user.contact_person_number}</td>
                                        <td className="p-3 text-slate-500 text-xs">{user.user_name}</td>
                                        <td className="p-3 text-slate-500 text-xs font-mono">{user.user_password}</td>
                                        <td className="p-3 text-slate-500 text-xs capitalize">{user.licence_type}</td>
                                        <td className="p-3 text-center"><StatusBadge status={user.status} /></td>
                                        <td className="p-3 text-center text-slate-500 text-xs">{formatDate(user.activation_date)}</td>
                                        <td className="p-3 text-center text-slate-500 text-xs">{formatDate(user.expire_date)}</td>
                                        <td className="p-3 text-center">
                                            <div className="flex gap-2 justify-center flex-wrap">
                                                <button
                                                    onClick={() => onToggleStatus(user)}
                                                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${normalizeStatus(user.status) === "active"
                                                        ? "text-rose-700 border border-rose-200 hover:bg-rose-100 bg-rose-50"
                                                        : "text-emerald-700 border border-emerald-200 hover:bg-emerald-100 bg-emerald-50"
                                                        }`}
                                                >
                                                    {normalizeStatus(user.status) === "active" ? "Deactivate" : "Activate"}
                                                </button>
                                                <button onClick={() => onViewDetails(user)} title="View full details" className="px-2.5 py-1 rounded-lg text-xs font-semibold text-indigo-700 border border-indigo-200 hover:bg-indigo-100 bg-indigo-50 transition-colors">
                                                    <InformationCircleIcon className="w-3.5 h-3.5 inline" />
                                                </button>
                                                <button onClick={() => onEditUser(user)} title="Edit" className="px-2.5 py-1 rounded-lg text-xs font-semibold text-amber-700 border border-amber-200 hover:bg-amber-100 bg-amber-50 transition-colors">
                                                    <PencilIcon className="w-3.5 h-3.5 inline" />
                                                </button>
                                                <button onClick={() => onViewHistory(user)} title="Activity & renewal history" className="px-2.5 py-1 rounded-lg text-xs font-semibold text-slate-700 border border-slate-200 hover:bg-slate-100 bg-slate-50 transition-colors">
                                                    <ClockIcon className="w-3.5 h-3.5 inline" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default UsersListModal;
